import { Injectable, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  baseAssignmentsActions,
  BaseCoreState,
  BaseModalService,
  BaseDateService,
  ContractResponse,
  BaseSigningStatusEnum,
  baseAssignmentsSelectors,
  BaseAssignmentsListPayloadInterface,
} from 'base';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter, map } from 'rxjs/operators';
import { take } from 'rxjs';

@Injectable()
export class ExternalEmployeeService {
  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private activatedRoute: ActivatedRoute
  ) {}

  setSignButtonClass(contract: ContractResponse, permission: boolean): string {
    const assignmentSignStatuses = this.setAssignmentSignStatus(contract, permission);

    return assignmentSignStatuses.isFullySigned
      ? 'state-btn-border-green no-pointer'
      : assignmentSignStatuses.isSignedByMyself
      ? 'state-btn-border-yellow no-pointer'
      : (assignmentSignStatuses.isNotSigned || assignmentSignStatuses.isSignedByPartner) &&
        assignmentSignStatuses.canBeSigned
      ? 'state-btn-border-red'
      : (assignmentSignStatuses.isNotSigned || assignmentSignStatuses.isSignedByPartner) &&
        !assignmentSignStatuses.canBeSigned
      ? 'state-btn-border-red no-pointer'
      : '';
  }

  openDigitalSignModal(modalRef: ElementRef): NgbModalRef {
    return this.modalService.open(
      {
        bodyTemplate: modalRef,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideCloseIcon: true,
        hideHeader: true,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );
  }

  showAssignmentModal(employeeInfoModalBody: ElementRef): NgbModalRef {
    return this.modalService.open(
      {
        hideHeader: true,
        hideFooter: true,
        bodyTemplate: employeeInfoModalBody,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
      },
      {
        size: 'xl',
      }
    );
  }

  updateInfoAfterSigning(): void {
    this.store.dispatch(
      baseAssignmentsActions.loadCustomerAllAssignments({
        payload: this.activatedRoute.snapshot.queryParams as BaseAssignmentsListPayloadInterface,
      })
    );
  }

  public setAssignmentTimeStatus(startDate: string, endDate: string) {
    return {
      isPlannedAssignment: BaseDateService.isBefore(new Date(), new Date(startDate)),
      isActiveAssignment: BaseDateService.isInsideAPeriod(new Date(), new Date(startDate), new Date(endDate)),
      isEndedAssignment: BaseDateService.isAfter(new Date(), new Date(endDate)),
    };
  }

  public setAssignmentSignStatus(contract: ContractResponse, hasSignContractPermission: boolean) {
    return {
      canBeSigned: this.canBeSigned(hasSignContractPermission, contract),
      isFullySigned: contract.signingStatus === BaseSigningStatusEnum.SIGNED,
      isSignedByPartner: contract.signingStatus === BaseSigningStatusEnum.SIGNED_BY_PSP,
      isSignedByMyself: contract.signingStatus === BaseSigningStatusEnum.SIGNED_BY_CUSTOMER,
      isNotSigned: contract.signingStatus === BaseSigningStatusEnum.UNSIGNED,
    };
  }

  public canBeSigned(hasSignContractPermission: boolean, contract: ContractResponse) {
    return (
      hasSignContractPermission &&
      contract.signatures?.statusCustomer !== 'signed' &&
      contract.signingStatus !== BaseSigningStatusEnum.SIGNED_BY_CUSTOMER &&
      contract.signingStatus !== BaseSigningStatusEnum.SIGNED
    );
  }

  getAssignment(id: number) {
    this.store.dispatch(baseAssignmentsActions.loadAssignment({ payload: { assignment: id } }));

    return this.store.select(baseAssignmentsSelectors.getAssignment).pipe(
      filter((state) => state.ok === true),
      map((state) => state.data),
      take(1)
    );
  }
}
